/**
 * Mint announcement events for Cashero.
 *
 * Each mint publishes an addressable event (kind 37691, d-tag "cashero-mint")
 * containing its public keysets, fee schedule, and terms. Wallets discover
 * mints by fetching this event for a given mint npub.
 */

import { type EventTemplate, type NostrEvent, finalizeEvent, getPublicKey } from 'nostr-tools';
import { hexToBytes } from '@noble/hashes/utils';

import { MINT_ANNOUNCEMENT_KIND, MINT_ANNOUNCEMENT_DTAG, MINT_DISCOVERY_TAG } from './constants';
import type { Keyset } from '@/lib/cashu/keyset';

/** Mint announcement content */
export interface MintAnnouncement {
  /** Mint display name */
  name: string;
  /** Short description */
  description: string;
  /** The asset unit ('piconero') */
  unit: string;
  /** Active keysets (public keys only) */
  keysets: Keyset[];
  /** Fee structure */
  fees: {
    /** Deposit fee as a percentage (e.g., 0.01 = 1%) */
    depositPercent: number;
    /** Withdraw flat fee in piconero */
    withdrawFlat: number;
  };
  /** Minimum block confirmations required for deposits */
  requiredConfirmations: number;
  /** Monero network ('mainnet') */
  moneroNetwork: string;
  /** Protocol version */
  version: string;
}

/**
 * Create a mint announcement event (kind 37691, signed).
 *
 * @param announcement - The mint's announcement data
 * @param mintPrivkeyHex - The mint's Nostr private key (hex)
 * @returns The signed Nostr event
 */
export function createMintAnnouncementEvent(
  announcement: MintAnnouncement,
  mintPrivkeyHex: string,
): NostrEvent {
  const privKeyBytes = hexToBytes(mintPrivkeyHex);
  const pubkey = getPublicKey(privKeyBytes);

  const template: EventTemplate = {
    kind: MINT_ANNOUNCEMENT_KIND,
    content: JSON.stringify(announcement),
    tags: [
      ['d', MINT_ANNOUNCEMENT_DTAG],
      ['t', MINT_DISCOVERY_TAG],
      ['name', announcement.name],
      // Add relay hints for the mint
    ],
    created_at: Math.floor(Date.now() / 1000),
    pubkey,
  };

  return finalizeEvent(template, privKeyBytes) as NostrEvent;
}

/**
 * Parse a mint announcement event.
 *
 * @param event - The kind 37691 Nostr event
 * @returns The parsed announcement
 * @throws If the event is invalid
 */
export function parseMintAnnouncementEvent(event: NostrEvent): MintAnnouncement {
  if (event.kind !== MINT_ANNOUNCEMENT_KIND) {
    throw new Error(`Invalid kind: expected ${MINT_ANNOUNCEMENT_KIND}, got ${event.kind}`);
  }

  const dTag = event.tags.find(([t]) => t === 'd')?.[1];
  if (dTag !== MINT_ANNOUNCEMENT_DTAG) {
    throw new Error(`Invalid d-tag: expected "${MINT_ANNOUNCEMENT_DTAG}", got "${dTag}"`);
  }

  let data: MintAnnouncement;
  try {
    data = JSON.parse(event.content);
  } catch {
    throw new Error('Invalid JSON in mint announcement content');
  }

  // Validate required fields
  if (!data.name) throw new Error('Mint announcement missing name');
  if (!data.unit) throw new Error('Mint announcement missing unit');
  if (!data.keysets || data.keysets.length === 0) {
    throw new Error('Mint announcement missing keysets');
  }
  if (!data.fees) throw new Error('Mint announcement missing fees');

  return {
    name: data.name,
    description: data.description ?? '',
    unit: data.unit,
    keysets: data.keysets,
    fees: {
      depositPercent: data.fees.depositPercent ?? 0.01,
      withdrawFlat: data.fees.withdrawFlat ?? 10000000, // 0.00001 XMR default
    },
    requiredConfirmations: data.requiredConfirmations ?? 1,
    moneroNetwork: data.moneroNetwork ?? 'mainnet',
    version: data.version ?? '1.0',
  };
}

/**
 * Build a Nostr filter to fetch a mint announcement for a given pubkey.
 */
export function getMintAnnouncementFilter(mintPubkey: string) {
  return {
    kinds: [MINT_ANNOUNCEMENT_KIND],
    authors: [mintPubkey],
    '#d': [MINT_ANNOUNCEMENT_DTAG],
    limit: 1,
  };
}

/**
 * Build a Nostr filter to discover all Cashero mints.
 */
export function getMintDiscoveryFilter() {
  return {
    kinds: [MINT_ANNOUNCEMENT_KIND],
    '#t': [MINT_DISCOVERY_TAG],
    limit: 100,
  };
}
